/** @jsxImportSource @emotion/react */
import classNames from 'classnames';
import React from 'react';
import { ClipLoader } from 'react-spinners';
import { AsyncState, AsyncStatus } from 'shared/utils/async-state';
import ImgNotFound from 'ud-ui/components/images/not-found.png';
import { DivProps } from 'typings';
import * as S from './styles';

interface ITableProps extends DivProps {
  /**
   * Состояние выдачи по стандарту `.claude/docs/async-loading-states.md`. Если передано,
   * ветку рендера выбирает оно, а `isLoading` игнорируется: только так у таблицы
   * появляется ветка ошибки, а рефетч перестаёт подменять уже показанные строки
   * спиннером — они остаются на месте приглушёнными (`isRefreshing`).
   */
  state?: AsyncState;
  /** @deprecated Считайте состояние через `deriveAsyncState` и передавайте `state`. */
  isLoading?: boolean;
  emptyImageSrc?: string;
  emptyText?: string;
  /** Что показать в ветке ошибки. Работает только вместе со `state`. */
  error?: React.ReactNode;
}

function isElementOfType(node: React.ReactNode, type: any): node is React.ReactElement {
  return React.isValidElement(node) && node.type === type;
}

function getColumnsCountFromThead(thead: React.ReactElement | undefined): number {
  if (!thead) return 1;

  // ищем первый <tr> внутри thead
  const headChildren = React.Children.toArray(thead.props.children);
  const firstRow = headChildren.find((c) => isElementOfType(c, 'tr')) as React.ReactElement | undefined;

  if (!firstRow) {
    // если thead обёрнут, пробуем взять первый валидный элемент и искать в нём tr
    const firstEl = headChildren.find((c) => React.isValidElement(c)) as React.ReactElement | undefined;
    if (firstEl) {
      const nested = React.Children.toArray(firstEl.props.children);
      const nestedRow = nested.find((c) => isElementOfType(c, 'tr')) as React.ReactElement | undefined;
      if (nestedRow) {
        const cells = React.Children.toArray(nestedRow.props.children);
        const count = cells.reduce((acc: number, cell) => {
          if (React.isValidElement(cell)) {
            const span = typeof cell.props?.colSpan === 'number' ? cell.props.colSpan : 1;
            return acc + span;
          }
          return acc + 1;
        }, 0);
        return count || 1;
      }
    }
    return 1;
  }

  // считаем th/td в первом tr
  const cells = React.Children.toArray(firstRow.props.children);
  const count = cells.reduce((acc: number, cell) => {
    if (React.isValidElement(cell)) {
      const span = typeof cell.props?.colSpan === 'number' ? cell.props.colSpan : 1;
      return acc + span;
    }
    return acc + 1;
  }, 0);

  return count || 1;
}

function Table(props: ITableProps) {
  const { state, isLoading, emptyImageSrc, emptyText, error, children, ...rest } = props;

  const childrenArray = React.Children.toArray(children);
  const theadEl = childrenArray.find((el) => isElementOfType(el, 'thead')) as React.ReactElement | undefined;
  const tbodyEl = childrenArray.find((el) => isElementOfType(el, 'tbody')) as React.ReactElement | undefined;
  const columnsCount = getColumnsCountFromThead(theadEl);

  // Определяем, есть ли строки в tbody
  const tbodyChildren = tbodyEl ? React.Children.toArray(tbodyEl.props.children) : [];
  const tbodyRowsCount = tbodyChildren.filter((c) => isElementOfType(c, 'tr')).length;

  // Без `state` работает прежняя логика двух булевых: спиннер или «Нет данных».
  const status: AsyncStatus = state ? state.status : isLoading ? 'loading' : tbodyRowsCount === 0 ? 'empty' : 'ready';
  const isRefreshing = Boolean(state?.isRefreshing);

  const fallback: React.ReactNode = {
    loading: <ClipLoader />,
    error: error ?? <span className="text-error text-sm">Не удалось загрузить данные</span>,
    empty: (
      <div className="flex flex-col items-center justify-center gap-2">
        <img src={emptyImageSrc ?? ImgNotFound} alt="" className="max-h-[200px] opacity-80" />
        <span className="text-inkSecondary text-sm">{emptyText ?? 'Нет данных'}</span>
      </div>
    ),
    ready: null,
  }[status];

  return (
    <S.Container {...rest}>
      <S.ScrollArea>
        <S.Table
          className={classNames('transition-opacity', { 'pointer-events-none opacity-50': isRefreshing })}
          aria-busy={isRefreshing}
        >
          {status !== 'ready' ? (
            <>
              {theadEl}
              <tbody>
                <tr>
                  <td colSpan={columnsCount}>
                    <div className="flex h-[300px] w-full items-center justify-center" role="status" aria-live="polite">
                      {fallback}
                    </div>
                  </td>
                </tr>
              </tbody>
            </>
          ) : (
            children
          )}
        </S.Table>
      </S.ScrollArea>
    </S.Container>
  );
}

export default Table;
